import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Share2, ChevronUp, ChevronDown, Volume2, VolumeX, Play, ArrowLeft, Plus, Link as LinkIcon, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = "/api";

const ShortCard = ({ short, isActive, onLike }) => {
  const [liked, setLiked] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      onLike(short.id);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: short.title, text: short.description, url: window.location.href });
    }
  };

  const isDirectVideo = short.embed_type === 'upload' || short.video_url?.match(/\.(mp4|webm|mov)(\?|$)/i);
  const isYouTube = short.embed_type === 'youtube' || short.video_url?.includes('youtube.com') || short.video_url?.includes('youtu.be');

  const getYouTubeEmbedUrl = (url) => {
    const match = url.match(/(?:shorts\/|watch\?v=|youtu\.be\/)([^&?\s]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&controls=0&modestbranding=1&playsinline=1` : url;
  };

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center">
      <div className="relative w-full max-w-md h-full">
        <div className="absolute inset-0">
          {isActive ? (
            isDirectVideo ? (
              <video
                ref={videoRef}
                src={short.video_url}
                className="w-full h-full object-cover"
                muted={muted}
                loop
                playsInline
                autoPlay
              />
            ) : isYouTube ? (
              <iframe
                src={getYouTubeEmbedUrl(short.video_url)}
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title={short.title}
              />
            ) : (
              <iframe
                src={short.video_url}
                className="w-full h-full"
                allow="autoplay"
                allowFullScreen
                title={short.title}
              />
            )
          ) : (
            <div className="relative w-full h-full">
              <img
                src={short.thumbnail || 'https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?w=400'}
                alt={short.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Play size={64} className="text-white/80" />
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        <div className="absolute bottom-0 left-0 right-0 p-4 pb-8">
          <h3 className="font-heading text-xl font-bold mb-2">{short.title}</h3>
          <p className="text-sm text-white/80 line-clamp-2">{short.description}</p>
          {short.tags && short.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {short.tags.map((tag) => (
                <span key={tag} className="text-xs text-primary">#{tag}</span>
              ))}
            </div>
          )}
        </div>

        <div className="absolute right-4 bottom-24 flex flex-col items-center gap-6">
          <button onClick={handleLike} className="flex flex-col items-center gap-1" data-testid={`like-btn-${short.id}`}>
            <div className={`p-3 rounded-full ${liked ? 'bg-primary' : 'bg-white/20'} transition-colors`}>
              <Heart size={24} className={liked ? 'fill-white text-white' : 'text-white'} />
            </div>
            <span className="text-xs">{(short.likes || 0) + (liked ? 1 : 0)}</span>
          </button>

          <button onClick={handleShare} className="flex flex-col items-center gap-1">
            <div className="p-3 rounded-full bg-white/20">
              <Share2 size={24} className="text-white" />
            </div>
            <span className="text-xs">Share</span>
          </button>

          <button onClick={() => setMuted(!muted)} className="flex flex-col items-center gap-1">
            <div className="p-3 rounded-full bg-white/20">
              {muted ? <VolumeX size={24} className="text-white" /> : <Volume2 size={24} className="text-white" />}
            </div>
            <span className="text-xs">{muted ? 'Unmute' : 'Mute'}</span>
          </button>
        </div>

        <div className="absolute top-4 right-4 bg-black/40 px-2 py-1 rounded-sm">
          <span className="text-xs">{(short.views || 0).toLocaleString()} views</span>
        </div>
      </div>
    </div>
  );
};

const Shorts = () => {
  const { user, token } = useAuth();
  const [shorts, setShorts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('embed');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const containerRef = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', video_url: '', embed_type: 'youtube', tags: '', file: null
  });

  useEffect(() => { fetchShorts(); }, []);

  const fetchShorts = async () => {
    try {
      const response = await axios.get(`${API}/shorts/random?limit=20`);
      setShorts(response.data);
    } catch {
      try {
        const fallback = await axios.get(`${API}/shorts`);
        setShorts(fallback.data);
      } catch { /* empty */ }
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (shortId) => {
    try { await axios.post(`${API}/shorts/${shortId}/like`); } catch { /* ignore */ }
  };

  const goToNext = useCallback(() => {
    if (currentIndex < shorts.length - 1) setCurrentIndex(p => p + 1);
  }, [currentIndex, shorts.length]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex(p => p - 1);
  }, [currentIndex]);

  useEffect(() => {
    const handleWheel = (e) => { if (e.deltaY > 50) goToNext(); else if (e.deltaY < -50) goToPrev(); };
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); goToNext(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); goToPrev(); }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('wheel', handleWheel); window.removeEventListener('keydown', handleKeyDown); };
  }, [goToNext, goToPrev]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }

    setSubmitting(true);
    try {
      let videoUrl = form.video_url;

      if (uploadMethod === 'file' && form.file) {
        // Upload to Cloudinary
        const sigRes = await axios.get(`${API}/cloudinary/signature`, {
          params: { resource_type: 'video', folder: 'shorts' },
          headers: { Authorization: `Bearer ${token}` }
        });
        const sig = sigRes.data;
        const formData = new FormData();
        formData.append('file', form.file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const cloudRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`,
          formData,
          { onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded * 100) / p.total)) }
        );
        videoUrl = cloudRes.data.secure_url;
      }

      if (!videoUrl) { toast.error('Please provide a video URL or upload a file'); return; }

      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await axios.post(`${API}/shorts`, {
        title: form.title,
        description: form.description,
        video_url: videoUrl,
        embed_type: uploadMethod === 'file' ? 'upload' : form.embed_type,
        tags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Short published!');
      setShowUpload(false);
      setForm({ title: '', description: '', video_url: '', embed_type: 'youtube', tags: '', file: null });
      setUploadProgress(0);
      fetchShorts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload short');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center" data-testid="shorts-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (shorts.length === 0 && !showUpload) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center" data-testid="shorts-empty">
        <div className="text-center">
          <Play size={48} className="mx-auto text-muted-foreground mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">No shorts yet</h2>
          <p className="text-muted-foreground mb-4">Be the first to share a short clip!</p>
          {user && (
            <Button onClick={() => setShowUpload(true)} className="gap-2">
              <Plus size={18} /> Upload Short
            </Button>
          )}
          <div className="mt-4">
            <Link to="/">
              <Button variant="ghost" className="gap-2"><ArrowLeft size={16} /> Back Home</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="fixed inset-0 bg-black overflow-hidden" data-testid="shorts-page">
      {/* Nav arrows */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-4">
        <Button variant="ghost" size="icon" onClick={goToPrev} disabled={currentIndex === 0}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="shorts-prev">
          <ChevronUp size={24} />
        </Button>
        <Button variant="ghost" size="icon" onClick={goToNext} disabled={currentIndex === shorts.length - 1}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="shorts-next">
          <ChevronDown size={24} />
        </Button>
      </div>

      {/* Progress indicators */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-1">
        {shorts.slice(0, 20).map((_, index) => (
          <div key={index} className={`w-1 h-4 rounded-full transition-colors ${index === currentIndex ? 'bg-primary' : 'bg-white/30'}`} />
        ))}
      </div>

      {/* Short content */}
      <AnimatePresence mode="wait">
        <motion.div key={currentIndex} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }} transition={{ duration: 0.3 }} className="w-full h-full">
          <ShortCard short={shorts[currentIndex]} isActive={true} onLike={handleLike} />
        </motion.div>
      </AnimatePresence>

      {/* Header */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-4">
        <Link to="/">
          <Button variant="ghost" size="icon" className="bg-white/10 hover:bg-white/20" data-testid="shorts-back">
            <ArrowLeft size={20} />
          </Button>
        </Link>
        <h1 className="font-heading text-xl font-bold">
          <span className="text-primary">TWIN</span><span className="text-secondary">TUBE</span>
          <span className="text-white/60 text-sm ml-2">Shorts</span>
        </h1>
      </div>

      {/* Upload button */}
      {user && (
        <div className="absolute top-4 right-4 z-20">
          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2 bg-primary/90 hover:bg-primary" data-testid="shorts-upload-btn">
                <Plus size={16} /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Short</DialogTitle>
                <DialogDescription>Share a short video clip with the community</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUploadSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Give your short a title" required data-testid="short-title-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this short about?" rows={2} data-testid="short-desc-input" />
                </div>

                {/* Upload method toggle */}
                <div className="flex gap-2">
                  <Button type="button" variant={uploadMethod === 'embed' ? 'default' : 'outline'} size="sm"
                    onClick={() => setUploadMethod('embed')} className="gap-2" data-testid="short-method-embed">
                    <LinkIcon size={14} /> Paste URL
                  </Button>
                  <Button type="button" variant={uploadMethod === 'file' ? 'default' : 'outline'} size="sm"
                    onClick={() => setUploadMethod('file')} className="gap-2" data-testid="short-method-file">
                    <Upload size={14} /> Upload File
                  </Button>
                </div>

                {uploadMethod === 'embed' ? (
                  <>
                    <div className="space-y-2">
                      <Label>Platform</Label>
                      <Select value={form.embed_type} onValueChange={(v) => setForm({ ...form, embed_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">YouTube</SelectItem>
                          <SelectItem value="tiktok">TikTok</SelectItem>
                          <SelectItem value="instagram">Instagram</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Video URL *</Label>
                      <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/shorts/..." data-testid="short-url-input" />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label>Video File *</Label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${form.file ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-primary'}`}
                      onClick={() => document.getElementById('short-file')?.click()}
                    >
                      {form.file ? (
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-sm">{form.file.name}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setForm({ ...form, file: null }); }}>
                            <X size={16} />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Upload className="mx-auto mb-2 text-muted-foreground" size={24} />
                          <p className="text-sm text-muted-foreground">Click to upload video</p>
                          <p className="text-xs text-muted-foreground mt-1">MP4, WebM (max 100MB)</p>
                        </>
                      )}
                    </div>
                    <input id="short-file" type="file" accept="video/*" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="music, live, minneapolis" data-testid="short-tags-input" />
                </div>

                {submitting && uploadMethod === 'file' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span>Uploading...</span><span>{uploadProgress}%</span></div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} data-testid="short-submit-btn">
                    {submitting ? <><Loader2 className="mr-2 animate-spin" size={16} /> Publishing...</> : 'Publish Short'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Counter */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-black/40 px-3 py-1 rounded-full">
        <span className="text-sm">{currentIndex + 1} / {shorts.length}</span>
      </div>
    </div>
  );
};

export default Shorts;
